import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import CircularProgress from '@mui/material/CircularProgress'
import { useDeleteReportMutation } from '@/core/services/report'
import { t, Trans } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'

type DeleteReportModalProps = {
  reportId: string
}

export default function DeleteReportModal({ reportId }: DeleteReportModalProps) {
  const [deleteReportById, { isLoading }] = useDeleteReportMutation()
  const [open, setOpen] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleDeleteReport = async () => {
    try {
      await deleteReportById(reportId).unwrap()
      enqueueSnackbar(t`Report deleted successfully`, { variant: 'success', autoHideDuration: 3000 })
      setOpen(false)
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error.message, t`An error occurred while deleting the report`), { variant: 'error', autoHideDuration: 3000 })
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  return (
    <React.Fragment>
      <Button variant="outlined" color="error" onClick={handleOpen}>
        <Trans>Delete</Trans>
      </Button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="delete-dialog-title">
        <DialogContent>
          <DialogContentText color="warning" id="alert-delete-report">{t`This report will be permanently deleted`}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>{t`Cancel`}</Button>
          <Button variant="contained" onClick={handleDeleteReport} disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} /> : undefined}>
            {isLoading ? <Trans>Deleting...</Trans> : <Trans>Agree</Trans>}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
